import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';

export const generateMotorcycleBodaBodaQuote = async (formData) => {
  try {
    // Fetch the template Excel file
    const response = await fetch('/Motor Cycle Boda.xlsx');
    const arrayBuffer = await response.arrayBuffer();
    
    // Load workbook using ExcelJS (preserves all formatting)
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    // Get the first worksheet
    const worksheet = workbook.worksheets[0];

    // Calculate values
    const sumInsured = parseFloat(formData.sumInsured);
    const rate = 0.04; // 4%
    let premium;
    if (formData.coverType === 'tpo' && formData.premiumBreakdown) {
      premium = formData.premiumBreakdown.basePremium;
    } else {
      premium = sumInsured * rate;
    }
    const fixedCharge = 500;
    const subtotal = premium + fixedCharge;
    const levies = premium * 0.0045; // 0.45% of premium
    const stampDuty = 40;
    const totalPremium = subtotal + levies + stampDuty;

    // Format current date
    const currentDate = new Date();
    const day = currentDate.getDate();
    const daySuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const formattedDate = `${day}${daySuffix(day)} ${monthNames[currentDate.getMonth()]}, ${currentDate.getFullYear()}`;

    // Update cells with new values (preserves all original formatting)
    worksheet.getCell('C7').value = formattedDate;
    worksheet.getCell('C8').value = formData.vehicleRegistration;
    worksheet.getCell('D8').value = sumInsured;
    worksheet.getCell('E8').value = '4%';
    worksheet.getCell('F8').value = premium;
    worksheet.getCell('F11').value = fixedCharge;
    worksheet.getCell('F28').value = subtotal;
    worksheet.getCell('F29').value = levies;
    worksheet.getCell('F30').value = stampDuty;
    worksheet.getCell('F32').value = totalPremium;
    worksheet.getCell('B35').value = 'Prepared By: New Digital Portal';

    // Extract additional text items from column G (EXCESS / REMARKS /CONDITIONS)
    const textItems = [];
    let startReading = false;
    for (let row = 1; row <= 100; row++) {
      const cellG = worksheet.getCell(`G${row}`);
      const cellValue = cellG.value;
      
      // Start reading after finding the header
      if (cellValue && typeof cellValue === 'string' && 
          (cellValue.includes('EXCESS') || cellValue.includes('REMARKS') || cellValue.includes('CONDITIONS'))) {
        startReading = true;
        continue;
      }
      
      // Read text items after header (skip empty cells, approval text, and standalone "Note")
      if (startReading && cellValue) {
        const text = typeof cellValue === 'string' ? cellValue.trim() : cellValue.toString().trim();
        if (text && text.length > 0 && 
            !text.toLowerCase().includes('approved by') && 
            !text.toLowerCase().includes('head of department') &&
            text.toLowerCase() !== 'note' &&
            text.toLowerCase() !== 'note:') {
          textItems.push({ row, col: 'G', text: text });
        }
      }
    }

    // Generate timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);

    // Generate PDF
    await generatePDF(formData, {
      sumInsured,
      rate,
      premium,
      fixedCharge,
      subtotal,
      levies,
      stampDuty,
      totalPremium,
      timestamp,
      textItems
    });

  } catch (error) {
    console.error('Error generating Motorcycle BodaBoda quote:', error);
    throw error;
  }
};

const generatePDF = async (formData, calculations) => {
  const doc = new jsPDF();
  
  // Load images
  const logo = await loadImage('/logo.png');
  const stamp = await loadImage('/stamp.png');
  
  // Add header background
  doc.setFillColor(15, 79, 72);
  doc.rect(0, 0, 210, 50, 'F');
  
  // Add logo (left side)
  if (logo) {
    doc.addImage(logo, 'PNG', 10, 5, 60, 30);
  }
  
  // Company contact info (right side)
  const rightX = 200;
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('ABSA Towers 1st floor, Loita Street', rightX, 12, { align: 'right' });
  doc.text('Nairobi, Kenya', rightX, 18, { align: 'right' });
  doc.text('+254 708 247 247 | +254 788 247 247', rightX, 24, { align: 'right' });
  doc.text('customerservice@definiteassurance.com', rightX, 30, { align: 'right' });
  
  // Title
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.text('QUOTATION', 105, 35, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'normal');
  doc.text(formData.productName || 'Motor Cycle BodaBoda', 105, 42, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Personal Information (left column)
  let yPos = 60;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Personal Information', 20, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 8;
  doc.text(`Name: ${formData.fullName || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Email: ${formData.email || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Phone: ${formData.phone || 'N/A'}`, 20, yPos);
  
  // Vehicle Information (right column)
  yPos = 60;
  doc.setFont(undefined, 'bold');
  doc.text('Vehicle Information', 115, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 8;
  doc.text(`Registration: ${formData.vehicleRegistration || 'N/A'}`, 115, yPos);
  yPos += 6;
  doc.text(`Year: ${formData.yearOfManufacture || 'N/A'}`, 115, yPos);
  yPos += 6;
  doc.text(`Value: KES ${calculations.sumInsured?.toLocaleString() || 'N/A'}`, 115, yPos);
  
  yPos = 95;

  // Premium breakdown table
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('Premium Breakdown', 20, yPos + 10);
  
  const tableData = [
    ['Basic Premium', `KES ${calculations.premium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Fixed Charge', `KES ${calculations.fixedCharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Subtotal', `KES ${calculations.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Training Levy (0.45%)', `KES ${calculations.levies.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Stamp Duty', `KES ${calculations.stampDuty.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
  ];

  // Draw manual table
  let tableY = yPos + 15;
  const leftMargin = 20;
  const colWidth1 = 100;
  const colWidth2 = 70;
  const rowHeight = 8;

  // Table header
  doc.setFillColor(220, 38, 38);
  doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Description', leftMargin + 2, tableY + 6);
  doc.text('Amount', leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });

  // Table body
  tableY += rowHeight;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');

  tableData.forEach((row, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
    }
    doc.text(row[0], leftMargin + 2, tableY + 6);
    doc.text(row[1], leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });
    tableY += rowHeight;
  });

  // Draw table border
  doc.setDrawColor(200, 200, 200);
  doc.rect(leftMargin, yPos + 15, colWidth1 + colWidth2, (tableData.length + 1) * rowHeight);

  // Total
  const finalY = tableY + 10;
  doc.setFillColor(220, 38, 38);
  doc.rect(leftMargin, finalY, colWidth1 + colWidth2, 15, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Total Annual Premium:', leftMargin + 5, finalY + 10);
  doc.text(`KES ${calculations.totalPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, leftMargin + colWidth1 + colWidth2 - 5, finalY + 10, { align: 'right' });

  // Footer
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.text('Prepared by: www.definiteassurance.com', leftMargin, finalY + 30);

  // Add additional text items from template
  if (calculations.textItems && calculations.textItems.length > 0) {
    doc.addPage();
    doc.setFillColor(15, 79, 72);
    doc.rect(0, 0, 210, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('EXCESS / REMARKS / CONDITIONS', 105, 10, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    let textY = 25;
    const lineHeight = 5;
    const maxWidth = 170;
    
    const drawTextWithBoldLimits = (text, x, y) => {
      if (text.includes('Limits')) {
        const parts = text.split('Limits');
        let currentX = x;
        
        parts.forEach((part, index) => {
          doc.setFont(undefined, 'normal');
          doc.text(part, currentX, y);
          currentX += doc.getTextWidth(part);
          
          if (index < parts.length - 1) {
            doc.setFont(undefined, 'bold');
            doc.text('Limits', currentX, y);
            currentX += doc.getTextWidth('Limits');
          }
        });
      } else {
        doc.setFont(undefined, 'normal');
        doc.text(text, x, y);
      }
    };
    
    calculations.textItems.forEach((item, index) => {
      // Split long text into multiple lines
      const lines = doc.splitTextToSize(item.text, maxWidth);
      lines.forEach(line => {
        if (textY > 280) {
          doc.addPage();
          textY = 20;
        }
        drawTextWithBoldLimits(line, 20, textY);
        textY += lineHeight;
      });
      textY += 2; // Extra space between items
    });

    // Add approval section at bottom of last page
    const bottomY = doc.internal.pageSize.height - 50;
    doc.setDrawColor(0);
    doc.line(20, bottomY, 190, bottomY);

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Approved by: K. Nyamu', 20, bottomY + 10);
    doc.text('Head of Department', 20, bottomY + 18);
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('© 2026 Definite Assurance. All rights reserved.', 105, 285, { align: 'center' });

  // Add stamp at bottom right with approval section
  if (stamp) {
    doc.addImage(stamp, 'PNG', 25, doc.internal.pageSize.height - 55, 35, 25);
  }

  // Save PDF
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const fileName = `Motorcycle_BodaBoda_Quote_${formData.vehicleRegistration.replace(/\s/g, '_')}_${timestamp}`;
  doc.save(`${fileName}.pdf`);
};

const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Helper function to load images
const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};
